import React, { useState, useEffect, useCallback } from 'react'
import cx from 'classnames'
import { currencyUtils } from '../../../../utils'
import { formatCurrencyInputValue } from 'utils/currency'
import TextField from '../../atoms/TextField/TextField'
import CurrencySymbol from '../../atoms/CurrencySymbol/CurrencySymbol'
import VatIcon from '../../atoms/VatIcon/VatIcon'
import styles from './CurrencyField.module.scss'
import VATCalculator from '../VATCalculator/VATCalculator'
import CurrencyInput from 'views/components/atoms/TextField/CurrencyInput'

type TProps = {
  id?: string
  className?: string
  disabled?: boolean
  label?: string
  labelHint?: string
  required?: boolean
  name?: string
  error?: string | React.ReactNode
  noOutline?: boolean
  dark?: boolean
  autoComplete?: string
  placeholder?: string
  currencySymbol?: string
  includeVatControl?: boolean
  includeVatCalculator?: boolean
  vatApplied?: boolean
  isEditVatDisabled?: boolean
  onToggleChange?: (vatApplied: boolean) => void
  onChange?: (event: any) => void
  setPaymentRuleAmount?: (event: any) => void
  value?: string | number
  allowNegative?: boolean
  inputTooltipText?: string
}

const CurrencyField: React.FC<TProps> = ({
  className,
  currencySymbol = 'R',
  includeVatControl = false,
  includeVatCalculator = true,
  vatApplied = false,
  isEditVatDisabled,
  onToggleChange = () => {},
  onChange = () => {},
  setPaymentRuleAmount = () => {},
  allowNegative = false,
  placeholder,
  inputTooltipText,
  value: propValue,
  disabled,
  labelHint,
  required,
  ...rest
}) => {
  const [editExAmount, setEditExAmount] = useState(false)
  const [value, setValue] = useState<number | string>(0)
  const [initialValue, setInitialValue] = useState<number | string>(0)

  // Initialize values on mount and when propValue changes
  useEffect(() => {
    const formattedValue = formatCurrencyInputValue(propValue)
    setValue(formattedValue)
    setInitialValue(formattedValue)
  }, [propValue])

  const handleChange = useCallback(
    (e: any) => {
      const { floatValue } = e

      setValue(floatValue)
      setInitialValue(floatValue)

      const currencyValue = currencyUtils.toCents(parseFloat(floatValue)).getAmount()
      onChange({
        ...e,
        target: { ...e.target, value: currencyValue },
      })
    },
    [onChange],
  )

  const handleToggleChange = useCallback(() => {
    if (onToggleChange) {
      onToggleChange(!vatApplied)
    }
    setEditExAmount(false)

    if (vatApplied) {
      setValue(initialValue)
    }
  }, [vatApplied, onToggleChange, initialValue])

  const leadingIcon = <CurrencySymbol>{currencySymbol}</CurrencySymbol>
  const isVatToggleDisabled = isEditVatDisabled ?? !!disabled

  const NOOP = () => {}
  const vatIconProps = {
    onClick: isVatToggleDisabled ? NOOP : handleToggleChange,
  }
  const trailingIcon = !includeVatControl ? null : <VatIcon enabled={vatApplied} {...vatIconProps} />

  return (
    <div className={cx([styles.root, className])}>
      <TextField
        id={rest.id}
        disabled={disabled}
        label={rest.label}
        labelHint={labelHint}
        required={required}
        name={rest.name}
        error={typeof rest.error === 'string' ? rest.error : undefined}
        noOutline={rest.noOutline}
        dark={rest.dark}
        inputComponent={
          <CurrencyInput
            value={value}
            disabled={disabled}
            leadingIcon={leadingIcon}
            trailingIcon={trailingIcon}
            trailingIconDisabled={isVatToggleDisabled}
            trailingIconInteractive={includeVatControl && !isVatToggleDisabled}
            placeholder={placeholder || '0 000.00'}
            autoComplete={rest.autoComplete as 'on' | 'off' | undefined}
            allowNegative={allowNegative}
            onChange={handleChange}
            inputTooltipText={inputTooltipText}
          />
        }
      />
      {includeVatCalculator && vatApplied && (
        <VATCalculator
          vatApplied={vatApplied}
          disabled={isVatToggleDisabled || !!disabled}
          currencySymbol="R"
          amount={typeof propValue === 'number' ? propValue : propValue ? parseFloat(propValue) : undefined}
          onChange={(amountInclVat: number) => {
            onChange({ target: { value: amountInclVat } })
          }}
        />
      )}
    </div>
  )
}

export default CurrencyField
